<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Btn, SvgIcon, ICON_TYPE } from '@jennifersoft/vue-components-v2';
import EmptyState from '@/features/analysis/agentUtility/components/EmptyState.vue';
import { chartFrameLoginMessages as messages } from '../i18n';
import { CHART_FRAME_STATUS_ICON_SIZE } from '../chart-frame-login-constants';
import type { ChartFrameLoginState } from '../chart-frame-login-types';

const props = defineProps<{
    state: ChartFrameLoginState;
    popupRetry: boolean;
    errorMessage: string | null;
}>();

defineEmits<{
    login: [];
    cancel: [];
}>();

const { t } = useI18n();
const isRetry = computed(
    () =>
        props.popupRetry ||
        ['blocked', 'closed', 'unavailable'].includes(props.state)
);
const title = computed(() =>
    t(...(props.state === 'complete' ? messages.signedIn : messages.title))
);
/* 진행·완료는 제목과 설명으로 충분하므로 다시 해야 할 때만 안내 문구를 띄운다. */
const statusMessage = computed(() => {
    if (props.state === 'blocked') return t(...messages.blocked);
    if (props.state === 'closed' || props.state === 'unavailable') {
        return t(...messages.notConfirmed);
    }
    return '';
});
</script>

<template>
    <section class="chart-frame-login" :aria-label="title">
        <EmptyState
            class="chart-frame-login__empty"
            :icon="state === 'complete' ? ICON_TYPE.check : ICON_TYPE.lock"
            :title="title"
            :description="
                popupRetry || state === 'complete'
                    ? undefined
                    : t(...messages.description)
            "
            transparent
        >
            <div v-if="state !== 'complete'" class="chart-frame-login__actions">
                <Btn
                    type="primary-filled"
                    size="large"
                    :leading-icon="
                        isRetry ? ICON_TYPE.refresh : ICON_TYPE.newWindow
                    "
                    :text="t(...(isRetry ? messages.retry : messages.login))"
                    @click="$emit('login')"
                />
                <Btn
                    v-if="popupRetry"
                    type="text"
                    size="large"
                    :text="t(...messages.cancel)"
                    @click="$emit('cancel')"
                />
            </div>
            <div
                v-if="errorMessage && state !== 'complete'"
                class="chart-frame-login__feedback chart-frame-login__feedback--error"
                role="alert"
            >
                <SvgIcon
                    :icon="ICON_TYPE.caution"
                    :width="CHART_FRAME_STATUS_ICON_SIZE"
                    :height="CHART_FRAME_STATUS_ICON_SIZE"
                    aria-hidden="true"
                />
                <span>{{ errorMessage }}</span>
            </div>
            <div
                class="chart-frame-login__status"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                <div
                    v-if="statusMessage"
                    class="chart-frame-login__feedback"
                    :class="{ 'chart-frame-login__feedback--notice': isRetry }"
                >
                    <SvgIcon
                        :icon="ICON_TYPE.info"
                        :width="CHART_FRAME_STATUS_ICON_SIZE"
                        :height="CHART_FRAME_STATUS_ICON_SIZE"
                        aria-hidden="true"
                    />
                    <span>{{ statusMessage }}</span>
                </div>
            </div>
        </EmptyState>
    </section>
</template>

<style scoped lang="scss">
.chart-frame-login {
    display: flex;
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    padding: 32px 24px;
    box-sizing: border-box;
    color: var(--gray-900);
    background: var(--surface-100);

    &__empty {
        min-height: 0;
        max-width: 440px;
        margin: auto;
    }

    :deep(.empty-state__body) {
        gap: 24px;
        padding: 0;
    }

    :deep(.empty-state__icon) {
        width: 40px;
        height: 40px;
    }

    :deep(.empty-state__title) {
        color: var(--gray-900);
        font-size: 20px;
        line-height: 28px;
        text-wrap: balance;
    }

    :deep(.empty-state__description) {
        max-width: 340px;
        text-wrap: balance;
        word-break: keep-all;
        overflow-wrap: anywhere;
    }

    :deep(.empty-state__actions) {
        width: 100%;
        margin-top: 0;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 8px;

        :deep(button) {
            max-width: 100%;
            min-height: 44px;
            height: auto;
            white-space: normal;
            text-align: center;
        }
    }

    &__feedback {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        gap: 8px;
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid var(--gray-200);
        font-size: 13px;
        line-height: 20px;
        color: var(--gray-600);
        text-align: start;
        overflow-wrap: anywhere;

        :deep(svg) {
            flex-shrink: 0;
            margin-top: 2px;
        }

        &--notice {
            color: var(--gray-700);
        }

        &--error {
            color: var(--red-700);
        }
    }
}

@media (max-width: 360px), (max-height: 320px) {
    .chart-frame-login {
        padding: 16px;

        :deep(.empty-state__body) {
            gap: 12px;
        }

        :deep(.empty-state__icon-wrapper) {
            width: 48px;
            height: 48px;
        }

        :deep(.empty-state__icon) {
            width: 24px;
            height: 24px;
        }

        :deep(.empty-state__title) {
            font-size: 18px;
            line-height: 26px;
        }

        &__feedback {
            margin-top: 16px;
            padding-top: 12px;
        }
    }
}
</style>
